import React, { useEffect, useReducer, useRef } from 'react';
import { useGetImportHistoryQuery } from '../../../../api/discovery';
import { useNotificationContext } from '../../../../providers/NotificationProvider';

const SUCCESS_STATUSES = new Set(['success', 'upload_success']);
const FAILURE_STATUSES = new Set(['error', 'upload_error', 'unknow']);
const BANNER_TIMEOUT_MS = 10_000;

function uploadStateReducer(state, action) {
    switch (action.type) {
        case 'RESOLVED':
            return { isWaitingForUpload: false, isPolling: false };
        case 'BANNER_TIMEOUT':
            return { ...state, isWaitingForUpload: false };
        default:
            return state;
    }
}

/**
 * Polls import history after an RVTools upload and surfaces success/failure
 * notifications.
 * */
export const useUploadDetection = ({
    orgId,
    activeImportId,
    hasPendingImport,
    onProcessingComplete,
    onProcessingFailed,
}) => {
    const activeImportIdRef = useRef(activeImportId);

    const [{ isWaitingForUpload, isPolling }, dispatch] = useReducer(uploadStateReducer, {
        isWaitingForUpload: !!hasPendingImport,
        isPolling: !!hasPendingImport,
    });

    const hasNotified = useRef(false);
    const hasRefreshed = useRef(false);

    const { notifySuccess, notifyError } = useNotificationContext();

    const callbacksRef = useRef({ onProcessingComplete, onProcessingFailed });
    useEffect(() => {
        callbacksRef.current = { onProcessingComplete, onProcessingFailed };
    });

    const { data: importHistory } = useGetImportHistoryQuery(
        { orgId, toolId: 'rv_tools' },
        {
            skip: !orgId || !isPolling,
            pollingInterval: isPolling ? 5000 : 0,
            refetchOnMountOrArgChange: true,
        },
    );

    useEffect(() => {
        if (!isPolling || hasNotified.current) return;
        if (!importHistory?.content?.length) return;

        const id = activeImportIdRef.current;
        const entry = id ? importHistory.content.find((e) => String(e.id) === String(id)) : importHistory.content[0];

        if (!entry) return;

        if (SUCCESS_STATUSES.has(entry.status)) {
            hasNotified.current = true;
            dispatch({ type: 'RESOLVED' });
            notifySuccess({
                message: 'Upload Successful',
                description: 'RVTools data has been successfully added to inventory.',
            });
            if (!hasRefreshed.current) {
                hasRefreshed.current = true;
                callbacksRef.current.onProcessingComplete?.();
            }
        } else if (FAILURE_STATUSES.has(entry.status)) {
            hasNotified.current = true;
            dispatch({ type: 'RESOLVED' });
            notifyError({
                message: 'Upload Failed',
                description: entry.error_details || 'An error occurred while processing the RVTools data.',
            });
            if (!hasRefreshed.current) {
                hasRefreshed.current = true;
                callbacksRef.current.onProcessingFailed?.();
            }
        }
    }, [importHistory, isPolling]);

    useEffect(() => {
        if (!hasPendingImport) return;
        const timer = setTimeout(() => {
            dispatch({ type: 'BANNER_TIMEOUT' });
            if (!hasRefreshed.current) {
                hasRefreshed.current = true;
                callbacksRef.current.onProcessingComplete?.();
            }
        }, BANNER_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, []);

    return { isWaitingForUpload };
};
